import { createSelector, createSlice } from "@reduxjs/toolkit";

export type ActiveFeatures =
  | "FBAccountAutoResponder"
  | "FBPageAutoResponder"
  | "callCapability"
  | "autoMarketing"
  | "campaign"
  | "supportInAllPages"
  | "FBMPDesktopApp"
  | "craigslistAds";

const initialState: {
  AIMode: boolean;
  activeConId: undefined;
  activeConName: { firstName: string; lastName: string };
  sortValue: string;
  dealerName: string;
  dealershipName: string;
  timeZone: string;
  openCreateLead: boolean;
  files: any;
  hasNewQuestion: boolean;
  isActiveExtension: boolean;
  uid: string;
  role: string;
  branchName: string | null;
  FBMarketplaceActive: string[];
  facebookActive: boolean;
  instagramActive: boolean;
  activeFeatures: ActiveFeatures[];
  notificationsCount: number;
} = {
  AIMode: true,
  activeConId: undefined,
  activeConName: { firstName: "", lastName: "" },
  sortValue: "All Message",
  dealerName: "",
  dealershipName: "",
  timeZone: localStorage.getItem("timeZone") || "",
  openCreateLead: false,
  files: [],
  hasNewQuestion: false,
  isActiveExtension: false,
  uid: "",
  role: "manager",
  branchName: localStorage.getItem("branchName"),
  facebookActive: false,
  FBMarketplaceActive: [],
  instagramActive: false,
  activeFeatures: [],
  notificationsCount: 0,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    filesSet: (state, { payload }) => {
      if (!payload) return;
      const newItem: any = payload;
      // Check if the item with the same ID already exists
      const exists = state.files.some((item: { id: any }) => item.id === newItem.id);
      if (exists) {
        return state; // If it exists, return the current state without changes
      }
      state.files.push(newItem);
    },
    openCreateLeadToggled: (state) => {
      state.openCreateLead = !state.openCreateLead;
    },
    isActiveExtensionToggled: (state, { payload }) => {
      state.isActiveExtension = payload;
    },
    dealerNameSet: (state, { payload }) => {
      state.dealerName = payload;
    },
    dealershipNameSet: (state, { payload }) => {
      state.dealershipName = payload;
    },
    timeZoneSet: (state, { payload }) => {
      localStorage.setItem("timeZone", payload);
      state.timeZone = payload;
    },
    branchNameSet: (state, { payload }) => {
      state.branchName = payload;
    },

    roleSet: (state, { payload }) => {
      state.role = payload;
    },
    sortValueSet: (state, { payload }) => {
      state.sortValue = payload;
    },
    activeConIdSet: (state, { payload }) => {
      state.activeConId = payload;
    },
    activeConNameSet: (state, { payload }) => {
      state.activeConName = payload;
    },
    AImodeSet: (state, { payload }) => {
      state.AIMode = payload;
    },
    facebookActiveSet: (state, { payload }) => {
      state.facebookActive = payload;
    },
    activeFeaturesSet: (state, { payload }) => {
      state.activeFeatures = payload;
    },
    FBMarketplaceActiveSet: (state, { payload }) => {
      state.FBMarketplaceActive = payload;
    },
    instagramActiveSet: (state, { payload }) => {
      state.instagramActive = payload;
    },
    setHasNewQuestion: (state, { payload }) => {
      state.hasNewQuestion = payload;
    },
    setNotificationsCount: (state, { payload }) => {
      state.notificationsCount = payload;
    },
  },
});

export const getFiles = createSelector(
  (state: any) => state.user,
  (user: any) => user.files
);

export const getOpenCreateLead = createSelector(
  (state: any) => state.user,
  (user: any) => user.openCreateLead
);

export const getActiveFeatures = createSelector(
  (state: any) => state.user,
  (user: any) => user.activeFeatures
);

export const getIsActiveExtensionToggled = createSelector(
  (state: any) => state.user,
  (user: any) => user.isActiveExtension
);

export const userInfo = createSelector(
  (state: any) => state.user,
  (user: any) => user.userInfo
);

export const getAIMode = createSelector(
  (state: any) => state.user,
  (user: any) => user.AIMode
);

export const getActiveConId = createSelector(
  (state: any) => state.user,
  (user: any) => user.activeConId
);

export const getSortValue = createSelector(
  (state: any) => state.user,
  (user: any) => user.sortValue
);

export const getDealerName = createSelector(
  (state: any) => state.user,
  (user: any) => user.dealerName
);

export const getDealershipName = createSelector(
  (state: any) => state.user,
  (user: any) => user.dealershipName
);

export const getTimeZone = createSelector(
  (state: any) => state.user,
  (user: any) => user.timeZone
);

export const getBranchName = createSelector(
  (state: any) => state.user,
  (user: any) => user.branchName
);

export const getRole = createSelector(
  (state: any) => state.user,
  (user: any) => user.role
);

export const getFacebookActive = createSelector(
  (state: any) => state.user,
  (user: any) => user.facebookActive
);

export const getFBMarketplaceActive = createSelector(
  (state: any) => state.user,
  (user: any) => user.FBMarketplaceActive
);

export const getInstagramActive = createSelector(
  (state: any) => state.user,
  (user: any) => user.instagramActive
);

export const getDealerUid = createSelector(
  (state: any) => state.user,
  (user: any) => user.uid
);

export const getHasNewQuestion = createSelector(
  (state: any) => state.user,
  (user: any) => user.hasNewQuestion
);

export const getNotificationsCount = createSelector(
  (state: any) => state.user,
  (user: any) => user.notificationsCount
);
export default userSlice.reducer;

export const {
  AImodeSet,
  activeConIdSet,
  activeConNameSet,
  sortValueSet,
  dealerNameSet,
  dealershipNameSet,
  roleSet,
  openCreateLeadToggled,
  filesSet,
  setHasNewQuestion,
  isActiveExtensionToggled,
  branchNameSet,
  facebookActiveSet,
  FBMarketplaceActiveSet,
  instagramActiveSet,
  timeZoneSet,
  activeFeaturesSet,
  setNotificationsCount,
} = userSlice.actions;
